/**
 * Компонент мониторинга статусов каналов для выбранной клиники
 * Показывает ВСЕ каналы с реальным статусом на основе webhook-активности
 * Дата: 30.12.2025 - Создан для отображения статусов каналов
 * Дата: 07.02.2026 - Переделан: показывает ВСЕ каналы, группирует по статусу
 * Дата: 07.02.2026 - v2: Мониторинг по webhook-активности через бэкенд API
 *
 * СВЯЗАННЫЕ МОДУЛИ:
 * - internal/handler/status_monitor.go — бэкенд endpoint GET /api/channels/status
 * - internal/domain/service/message/service.go — GetChannelLastActivity()
 * - internal/repository/postgres/message.go — GetChannelLastActivity() SQL
 * - frontend/src/pages/Messages.tsx — использует компонент
 *
 * СХЕМА:
 * - Композиция функций:
 *   ChannelStatusMonitor(tenantId)
 *     → useQuery('channels-status') → GET /api/channels/status?tenant_id=X
 *     → render: active (зелёные), configured (жёлтые), inactive (серые)
 * - Статусы:
 *   active — настроен + webhook за последние 48h
 *   configured — настроен, но нет недавних webhook
 *   inactive — не настроен (нет ключей)
 * - Использование из других модулей:
 *   - MessagesPage → sidebar header, после TenantSelector
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { CheckCircle2, XCircle, AlertCircle, Wifi, WifiOff, Clock } from 'lucide-react';

// Дата: 07.02.2026 - DTO от бэкенда GET /api/channels/status
interface ChannelStatusFromAPI {
  type: string;        // whatsapp_waba, whatsapp_regular, telegram_bot, telegram_client, max, sms
  name: string;        // Человекочитаемое имя
  configured: boolean; // Есть ли ключи в конфиге
  last_webhook_at: string | null;  // ISO timestamp последнего входящего webhook
  last_outgoing_at: string | null; // ISO timestamp последнего исходящего
  total_messages: number;          // Общее количество сообщений
  status: 'active' | 'configured' | 'inactive'; // Реальный статус
  details: string;     // Детали (username бота, channel_id и т.д.)
}

interface ChannelStatusMonitorProps {
  tenantId?: string;
}

// Короткие имена для компактных бейджей
const SHORT_NAMES: Record<string, string> = {
  whatsapp_waba: 'WABA',
  whatsapp_regular: 'Regular',
  telegram_bot: 'TG Бот',
  telegram_client: 'TG Клиент',
  max: 'MAX',
  max_client: 'Max Cli',
  sms: 'SMS',
};

// Форматирование относительного времени
function formatRelativeTime(isoString: string | null): string {
  if (!isoString) return 'никогда';
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;
  if (diffHours < 24) return `${diffHours}ч назад`;
  if (diffDays < 7) return `${diffDays}д назад`;
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

export function ChannelStatusMonitor({ tenantId }: ChannelStatusMonitorProps) {
  // Дата: 07.02.2026 - Запрос к бэкенд API для получения реального статуса каналов
  const { data: channelsData, isLoading } = useQuery({
    queryKey: ['channels-status', tenantId],
    queryFn: async () => {
      if (!tenantId) return null;
      try {
        const response = await apiClient.get(`/channels/status?tenant_id=${tenantId}`);
        return response.data as { channels: ChannelStatusFromAPI[] };
      } catch (error) {
        console.error('Failed to fetch channels status:', error);
        return null;
      }
    },
    enabled: !!tenantId,
    staleTime: 30_000, // Кеш на 30 секунд
    refetchInterval: 60_000, // Автообновление раз в минуту
  });

  if (!tenantId || isLoading || !channelsData?.channels) {
    return null;
  }

  const channels = channelsData.channels;

  // Группируем по статусу
  const active = channels.filter((ch) => ch.status === 'active');
  const configured = channels.filter((ch) => ch.status === 'configured');
  const inactive = channels.filter((ch) => ch.status === 'inactive');

  return (
    <div className="px-4 py-2.5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
      {/* Активные — зелёные (есть webhook за 48h) */}
      {active.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-1">
          <Wifi className="w-3 h-3 text-emerald-500 flex-shrink-0" />
          <span className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider flex-shrink-0">
            Активны:
          </span>
          {active.map((ch) => (
            <div
              key={ch.type}
              className="group relative flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 border border-emerald-100"
            >
              <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" />
              <span className="text-[10px] font-medium text-emerald-700">
                {SHORT_NAMES[ch.type] || ch.name}
              </span>
              {ch.total_messages > 0 && (
                <span className="text-[9px] text-emerald-500 font-mono">{ch.total_messages}</span>
              )}
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                <div className="font-semibold">{ch.name}</div>
                {ch.last_webhook_at && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Clock className="w-2.5 h-2.5" />
                    Webhook: {formatRelativeTime(ch.last_webhook_at)}
                  </div>
                )}
                {ch.details && <div className="text-slate-300 mt-0.5">{ch.details}</div>}
                <div className="text-slate-300">Всего: {ch.total_messages} сообщ.</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Настроены, но нет недавней активности — жёлтые */}
      {configured.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap mb-1">
          <AlertCircle className="w-3 h-3 text-amber-500 flex-shrink-0" />
          <span className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider flex-shrink-0">
            Настроены:
          </span>
          {configured.map((ch) => (
            <div
              key={ch.type}
              className="group relative flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 border border-amber-200"
            >
              <AlertCircle className="w-2.5 h-2.5 text-amber-500" />
              <span className="text-[10px] font-medium text-amber-700">
                {SHORT_NAMES[ch.type] || ch.name}
              </span>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                <div className="font-semibold">{ch.name}</div>
                <div className="text-amber-300">Настроен, нет webhook &gt;48ч</div>
                {ch.last_webhook_at && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <Clock className="w-2.5 h-2.5" />
                    Последний: {formatRelativeTime(ch.last_webhook_at)}
                  </div>
                )}
                {ch.details && <div className="text-slate-300 mt-0.5">{ch.details}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Не настроены — серые */}
      {inactive.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <WifiOff className="w-3 h-3 text-slate-400 flex-shrink-0" />
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider flex-shrink-0">
            Не подключены:
          </span>
          {inactive.map((ch) => (
            <div
              key={ch.type}
              className="group relative flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-100 border border-slate-200"
            >
              <XCircle className="w-2.5 h-2.5 text-slate-400" />
              <span className="text-[10px] font-medium text-slate-500">
                {SHORT_NAMES[ch.type] || ch.name}
              </span>
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                {ch.name}: Не настроен
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
